import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { format, addDays, differenceInCalendarDays } from "date-fns";

export interface CreateDebtInput {
  user_id: string;
  gym_id: string;
  category_id: string | null;
  category_name: string;
  category_validity_days: number;
  total_amount: number;
  paid_amount: number;
  grace_days: number;
  due_date: string; // YYYY-MM-DD
  payment_method?: string;
}

export const useCreateDebt = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: async (input: CreateDebtInput) => {
      const owed = Math.max(0, input.total_amount - input.paid_amount);
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) throw new Error("No autenticado");

      // 1) Si hay un monto pagado inicial, registrar el pago en payments
      let initialPaymentId: string | null = null;
      if (input.paid_amount > 0) {
        const todayIso = `${format(new Date(), "yyyy-MM-dd")}T12:00:00.000Z`;
        const { data: payment, error: payErr } = await supabase
          .from("payments")
          .insert({
            user_id: input.user_id,
            amount: input.paid_amount,
            description: `Pago parcial deuda — ${input.category_name}`,
            paid_at: todayIso,
            validity_days: input.category_validity_days,
            payment_method: input.payment_method || null,
          })
          .select()
          .single();
        if (payErr) throw payErr;
        initialPaymentId = payment.id;
      }

      // 2) Crear la deuda
      const { data, error: err } = await supabase
        .from("member_debts")
        .insert({
          user_id: input.user_id,
          gym_id: input.gym_id,
          category_id: input.category_id,
          category_name: input.category_name,
          category_validity_days: input.category_validity_days,
          total_amount: input.total_amount,
          paid_amount: input.paid_amount,
          owed_amount: owed,
          grace_days: input.grace_days,
          due_date: input.due_date,
          status: "active",
          created_by: auth.user.id,
          settlement_payment_id: initialPaymentId,
        })
        .select()
        .single();
      if (err) throw err;
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["member-debts-by-users"] });
      queryClient.invalidateQueries({ queryKey: ["gym-debtors", vars.gym_id] });
      if (vars.paid_amount > 0) {
        queryClient.invalidateQueries({ queryKey: ["payments"] });
        queryClient.invalidateQueries({ queryKey: ["lastPayment", vars.user_id] });
        queryClient.invalidateQueries({ queryKey: ["user-payments"] });
        queryClient.invalidateQueries({
          predicate: (q) => {
            const k = q.queryKey?.[0];
            return k === "arqueo-payments" || k === "arqueo-closure";
          },
        });
      }
      success("Deuda creada", {
        description:
          vars.paid_amount > 0
            ? "Se registró el pago parcial y la deuda quedó activa."
            : "El socio quedó marcado como En deuda.",
      });
    },
    onError: (e: any) => {
      console.error("Error creating debt:", e);
      error(e?.message || "No se pudo crear la deuda");
    },
  });
};

export interface SettleDebtInput {
  debt_id: string;
  user_id: string;
  gym_id: string;
  owed_amount: number;
  category_name: string;
  new_expiration_date: string; // YYYY-MM-DD
  payment_method: string;
}

export const useSettleDebt = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: async (input: SettleDebtInput) => {
      const today = new Date();
      const todayStr = format(today, "yyyy-MM-dd");
      const expiration = new Date(input.new_expiration_date + "T00:00:00");
      const validityDays = Math.max(1, differenceInCalendarDays(expiration, today));

      // 1) Crear pago por monto adeudado
      const { data: payment, error: payErr } = await supabase
        .from("payments")
        .insert({
          user_id: input.user_id,
          amount: input.owed_amount,
          description: `Cancelación deuda — ${input.category_name}`,
          paid_at: `${todayStr}T12:00:00.000Z`,
          validity_days: validityDays,
          payment_method: input.payment_method,
        })
        .select()
        .single();
      if (payErr) throw payErr;

      // 2) Marcar deuda como saldada
      const { error: updErr } = await supabase
        .from("member_debts")
        .update({
          status: "settled",
          settled_at: new Date().toISOString(),
          settlement_payment_id: payment.id,
        })
        .eq("id", input.debt_id);
      if (updErr) throw updErr;

      return payment;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["member-debts-by-users"] });
      queryClient.invalidateQueries({ queryKey: ["gym-debtors", vars.gym_id] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["lastPayment", vars.user_id] });
      queryClient.invalidateQueries({ queryKey: ["user-payments"] });
      success("Deuda cancelada", { description: "Se registró el pago y se actualizó la membresía." });
    },
    onError: (e: any) => {
      console.error("Error settling debt:", e);
      error(e?.message || "No se pudo cancelar la deuda");
    },
  });
};

export const _utils = { addDays };
